import _styled, { default as _styled2 } from "styled-components";
const _excluded = ["style", "className", "error", "disabled", "maxHeight", "minHeight", "onChange", "value", "preventAutoHeight", "softLimit", "maxLength", "showCountThreshold", "defaultValue", "showCount", "inputStyle", "inputClassName"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React, { useCallback, useLayoutEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styledC from 'styled-components';
import { useAutoHeight, useForwardedRef } from '@adsk/alloy-react-helpers';
import theme, { getInteractiveStyles, stylePropType } from '@adsk/alloy-react-theme';
const StyledCount = styledC.div.withConfig({
  displayName: "Textarea__StyledCount",
  componentId: "sc-14iv2iq-0"
})(["color:", ";pointer-events:none;position:absolute;right:", "px;bottom:", "px;", " ", ""], theme.tokens.colors.text.secondary.value, theme.spacing.XXS, theme.spacing.XXS, props => props.disabled && `color: ${theme.tokens.colors.text.inactive};`, props => props.$error && `color: ${theme.tokens.colors.text.error.value};`);
/**
 * Textarea component that expands with text content.
 * All arbitrary DOM properties and attributes (including event handlers) are accepted and passed to HTML textarea element.
 */
const Textarea = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      style,
      className,
      error = false,
      disabled = false,
      maxHeight = '100%',
      minHeight = 36,
      onChange,
      value,
      preventAutoHeight,
      softLimit,
      maxLength,
      showCountThreshold = 15,
      defaultValue,
      showCount,
      inputStyle,
      inputClassName
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const textareaRef = useForwardedRef(ref);
  const [height, updateHeight] = useAutoHeight(textareaRef);
  const [charCount, setCharCount] = useState(0);
  const limit = useMemo(() => showCount !== false && (softLimit || maxLength), [showCount, softLimit, maxLength]);
  const showLimit = useMemo(() => limit && (!showCountThreshold || showCountThreshold && limit - charCount <= showCountThreshold), [limit, showCountThreshold, charCount]);
  useLayoutEffect(() => {
    if (!preventAutoHeight) {
      updateHeight();
    }
  }, [preventAutoHeight, updateHeight]);
  useLayoutEffect(() => {
    if (value !== undefined && value !== null) {
      setCharCount(value.length);
    }
  }, [value]);
  useLayoutEffect(() => {
    if (defaultValue !== undefined && defaultValue !== null) {
      setCharCount(defaultValue.toString().length);
    }
    // we only want to do this on mount since it's the default value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleChange = useCallback(event => {
    if (value === undefined || value === null) {
      if (event.target.value) {
        setCharCount(event.target.value.length);
      } else {
        setCharCount(0);
      }
    }
    updateHeight();
    onChange === null || onChange === void 0 ? void 0 : onChange(event);
  }, [onChange, updateHeight, value]);
  return /*#__PURE__*/React.createElement(_StyledDiv, {
    className: className,
    $_css: [_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, theme.typography.labelMedium), {}, {
      color: theme.tokens.colors.text.primary.value,
      border: `1px solid ${theme.tokens.colors.border.value}`,
      borderRadius: theme.borderRadius.small,
      boxSizing: 'border-box',
      height,
      maxHeight,
      minHeight,
      position: 'relative',
      margin: 0,
      padding: 0
    }, getInteractiveStyles(disabled)), disabled && {
      background: theme.tokens.colors.background.neutral.inactive.value,
      borderColor: theme.tokens.colors.border.inactive.value,
      boxShadow: 'none',
      color: theme.tokens.colors.text.inactive.value,
      cursor: 'not-allowed'
    }), !error && {
      '&:focus:not(:active)': {
        borderColor: theme.tokens.colors.border.focused.value
      }
    }), error && {
      borderColor: theme.tokens.colors.border.error.value
    }), showLimit && {
      paddingBottom: 28
    }), style]
  }, /*#__PURE__*/React.createElement(_StyledTextarea, _extends({
    className: inputClassName,
    disabled: disabled,
    ref: textareaRef,
    value: value,
    defaultValue: defaultValue,
    onChange: handleChange,
    maxLength: maxLength
  }, props, {
    $_css2: [_objectSpread(_objectSpread({
      outline: 'none',
      resize: 'none',
      border: 'none',
      overflow: 'auto',
      height: '100% !important',
      width: '100%',
      padding: `${theme.spacing.XS}px ${theme.spacing.S}px`
    }, !showLimit && getInteractiveStyles(disabled)), {}, {
      '&::placeholder': {
        color: theme.tokens.colors.text.tertiary.value
      },
      '&:disabled': {
        background: theme.tokens.colors.background.neutral.inactive.value,
        color: theme.tokens.colors.text.inactive.value,
        cursor: 'not-allowed',
        boxShadow: 'none'
      }
    }), inputStyle]
  })), !!showLimit && /*#__PURE__*/React.createElement(StyledCount, {
    disabled: disabled,
    $error: error,
    "data-testid": "Textarea__count"
  }, `${charCount}/${limit}`));
});
Textarea.displayName = 'Textarea';
Textarea.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Toggle error state */
  error: PropTypes.bool,
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** Max height in pixels or percent (applied via CSS) */
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Min height in pixels or percent (applied via CSS) */
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** String value of the textarea */
  value: PropTypes.string,
  /** Function executed on text content change */
  onChange: PropTypes.func,
  /** Text to display when input is empty */
  placeholder: PropTypes.string,
  /** Like maxLength but this is a soft threshold and the input does not stop the user from entering characters beyond this amount, but this will show as the maximum number in the count display */
  softLimit: PropTypes.number,
  /** A hard limit on the maximum number of characters permitted in the input field, if softLimit is not provided then this will show as the maximum number in the count display */
  maxLength: PropTypes.number,
  /** If set, this will delay the display of character count until the character count is within this many characters of reaching the maximum length or soft limit length. Default is 15. Set to 0 to always show the count when showCount is enabled. */
  showCountThreshold: PropTypes.number,
  /** If true and maxLength or softLimit is set then display a character count, otherwise do not display the character count */
  showCount: PropTypes.bool,
  /** Styles applied to the input element */
  inputStyle: stylePropType,
  /** Class applied to the input element */
  inputClassName: PropTypes.string,
  /** If true, prevents the textarea from automatically resizing based on content */
  preventAutoHeight: PropTypes.bool
};
export default Textarea;
var _StyledDiv = styledC("div").withConfig({
  displayName: "Textarea___StyledDiv",
  componentId: "sc-14iv2iq-1"
})(["", ""], p => p.$_css);
var _StyledTextarea = styledC("textarea").withConfig({
  displayName: "Textarea___StyledTextarea",
  componentId: "sc-14iv2iq-2"
})(["", ""], p => p.$_css2);
//# sourceMappingURL=Textarea.js.map